import { ResourceIdentifierFn } from '@interacta-shared/data-access-storage-temporary-link';
import { TemporaryLinks } from '@interacta-shared/util-common';

/**
 * Shell-specific resource identifier function for parsing post attachment URLs.
 *
 * This function parses URLs that follow the shell application's post attachment
 * URL pattern and extracts resource information needed for temporary link generation.
 *
 * Supported URL pattern:
 * `[prefix]/communications/post-attachments/{entityId}/{contentRef}[suffix]`
 *
 * Supported suffixes and their corresponding link types:
 * - `` (empty) - View or download link (determined by response-content-disposition)
 * - `-preview` - Preview image link
 * - `-view-optimization` - High-resolution preview image link
 * - `-view-optimization-animated` - High-resolution animated preview image link
 *
 * @param url - The URL to parse for resource information
 * @returns ResourceIdentifier if URL matches the expected pattern, null otherwise
 *
 * @example
 * ```typescript
 * // View/download link
 * const url1 = 'https://api.example.com/communications/post-attachments/123/abc123def';
 * const result1 = shellResourceIdentifierFn(url1);
 * // Returns: { entityId: 123, entityType: 'post', contentRef: 'abc123def', temporaryLinkKind: 'temporaryContentViewLink' }
 *
 * // Preview image link
 * const url2 = 'https://api.example.com/communications/post-attachments/456/def456ghi-preview';
 * const result2 = shellResourceIdentifierFn(url2);
 * // Returns: { entityId: 456, entityType: 'post', contentRef: 'def456ghi', temporaryLinkKind: 'temporaryContentPreviewImageLink' }
 * ```
 */
export const shellResourceIdentifierFn: ResourceIdentifierFn = (
    url: string,
) => {
    try {
        // Parse the URL to extract components
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;

        // Match the pattern: [prefix]/communications/post-attachments/{entityId}/{contentRef}[suffix]
        const pathRegex =
            /^(.*)\/communications\/post-attachments\/(\d+)\/([a-f0-9]+)(.*)$/;
        const match = pathname.match(pathRegex);

        if (!match) {
            return null;
        }

        const [, , entityIdStr, contentRef, suffix] = match;
        const entityId = parseInt(entityIdStr, 10);

        if (isNaN(entityId)) {
            return null;
        }

        // Determine the temporary link kind based on suffix and query parameters
        let temporaryLinkKind: keyof TemporaryLinks;

        switch (suffix) {
            case '': {
                // Check response-content-disposition to distinguish between view and download
                const disposition = urlObj.searchParams.get(
                    'response-content-disposition',
                );
                if (disposition?.includes('attachment')) {
                    temporaryLinkKind = 'temporaryContentDownloadLink';
                } else {
                    temporaryLinkKind = 'temporaryContentViewLink';
                }
                break;
            }
            case '-preview': {
                temporaryLinkKind = 'temporaryContentPreviewImageLink';
                break;
            }
            // currently temporaryContentPreviewImageAnimatedLink is never used
            //
            // case '-preview-animated': {
            //     temporaryLinkKind = 'temporaryContentPreviewImageAnimatedLink';
            //     break;
            // }
            case '-view-optimization': {
                temporaryLinkKind = 'temporaryContentPreviewImageHiResLink';
                break;
            }
            case '-view-optimization-animated': {
                temporaryLinkKind =
                    'temporaryContentPreviewImageHiResAnimatedLink';
                break;
            }
            default: {
                return null;
            }
        }
        return {
            entityId,
            entityType: 'post', // Currently only post entityType is managed TODO IISP-12055
            contentRef,
            temporaryLinkKind,
        };
    } catch (error) {
        // If URL parsing fails or any other error occurs, return null
        return null;
    }
};
